import { useSyncExternalStore } from 'react';
/**
 * Module-level store for global loader state.
 * This ensures that document-level event listeners are attached only once,
 * and all hook consumers subscribe to the same shared state.
 */
let loaderState = false;
const loaderSubscribers = new Set();
let listenersAttached = false;
/**
 * Notify all subscribers that the loader state has changed.
 */
const notifyLoaderSubscribers = () => {
  loaderSubscribers?.forEach((listener) => {
    listener?.();
  });
};
/**
 * Handler to show the loader.
 */
const showLoaderHandler = () => {
  loaderState = true;
  notifyLoaderSubscribers();
};
/**
 * Handler to hide the loader.
 */
const hideLoaderHandler = () => {
  loaderState = false;
  notifyLoaderSubscribers();
};
/**
 * Attach document-level event listeners once, when needed.
 */
const attachDocumentListeners = () => {
  if (listenersAttached === true) {
    return;
  }
  if (typeof document !== 'undefined') {
    document?.addEventListener('showLoader', showLoaderHandler, false);
    document?.addEventListener('hideLoader', hideLoaderHandler, false);
    listenersAttached = true;
  }
};
/**
 * Detach document-level event listeners when there are no subscribers.
 */
const detachDocumentListeners = () => {
  if (listenersAttached !== true) {
    return;
  }
  if (typeof document !== 'undefined') {
    document?.removeEventListener('showLoader', showLoaderHandler);
    document?.removeEventListener('hideLoader', hideLoaderHandler);
    listenersAttached = false;
  }
};
/**
 * Subscription function for useSyncExternalStore.
 * Adds the listener and manages attachment of document listeners.
 */
const subscribeToLoaderStore = (listener) => {
  loaderSubscribers?.add(listener);
  // Attach document listeners when the first subscriber is added.
  if (loaderSubscribers?.size === 1) {
    attachDocumentListeners();
  }
  // Return unsubscribe function.
  return () => {
    loaderSubscribers?.delete(listener);
    // Detach document listeners when there are no more subscribers.
    if (loaderSubscribers?.size === 0) {
      detachDocumentListeners();
    }
  };
};
/**
 * Snapshot getter for useSyncExternalStore (client).
 */
const getLoaderSnapshot = () => {
  return loaderState;
};
/**
 * Snapshot getter for useSyncExternalStore (server).
 * Returns a safe default value for server-side rendering.
 */
const getLoaderServerSnapshot = () => {
  return false;
};

const useGlobalLoader = () => {
  // Subscribe to the shared loader store using React's recommended API

  const isLoading = useSyncExternalStore(
    subscribeToLoaderStore,
    getLoaderSnapshot,
    getLoaderServerSnapshot
  );
  return { isLoading, showLoaderHandler, hideLoaderHandler };
};

export default useGlobalLoader;
